import { getAccountConfig } from 'components/account/AccountProvider';
import { Button } from 'components/Button';
import { FormField } from 'components/form/FormField';
import { FormGroup } from 'components/form/FormGroup';
import { FormInputPicker } from 'components/form/FormInputPicker';
import { FormSwitch } from 'components/form/FormSwitch';
import { useValue } from 'components/form/hook/useValue';
import { Link } from 'components/Link';
import { SaleItemsListLightQuery } from 'graphql/SaleItem/queries/SaleItemsListLightQuery';
import { isOptionLike, OPTION_LIST_CONFIGURATIONS, PRODUCT_LIST_CONFIGURATIONS } from 'helpers/optionConfiguration';
import { React, useMemo, useState, useWatch } from 'libs/react';
import { useSaleItemPickerFormat } from 'modules/catalog/saleItem/hooks/useSaleItemPickerFormat';
import './SaleItemAvailabilitySection.scss';
/**
 * Champs de disponibilité de la fiche d'une option ou d'un produit :
 * réservation exclusive et liens « Réserve aussi ». Le titre de section
 * est porté par le formulaire parent.
 * @component SaleItemAvailabilitySection
 * @since 1.0.0
 */
export function SaleItemAvailabilitySection(props) {
    var _a, _b, _c;
    const { data } = props;
    const primarySaleItemInfo = getAccountConfig('PRIMARY_SALE_ITEM_INFO');
    const format = useSaleItemPickerFormat();
    const [items, setItems] = useState((_a = data === null || data === void 0 ? void 0 : data.alsoBooks) !== null && _a !== void 0 ? _a : []);
    useWatch(() => {
        var _a;
        setItems((_a = data === null || data === void 0 ? void 0 : data.alsoBooks) !== null && _a !== void 0 ? _a : []);
    }, [data === null || data === void 0 ? void 0 : data.alsoBooks]);
    const ids = useMemo(() => items.map((item) => item.id), [items]);
    useValue('alsoBooks', ids, []);
    const bookedBy = (_b = data === null || data === void 0 ? void 0 : data.alsoBookedBy) !== null && _b !== void 0 ? _b : [];
    const linked = items.length > 0 || bookedBy.length > 0;
    /**
     * @function add
     * @since 1.0.0
     * @hidden
     */
    function add(added) {
        setItems((current) => [
            ...current,
            ...added.filter((item) => item.id !== (data === null || data === void 0 ? void 0 : data.id) && !current.some((other) => other.id === item.id))
        ]);
    }
    /**
     * @function remove
     * @since 1.0.0
     * @hidden
     */
    function remove(removed) {
        const removedIds = removed.map((item) => item.id);
        setItems((current) => current.filter((item) => !removedIds.includes(item.id)));
    }
    /**
     * @function renderItem
     * @since 1.0.0
     * @hidden
     */
    function renderItem(item) {
        const label = format(item);
        return (React.createElement(Link, { className: "sale-item-availability-section-link", route: {
                name: isOptionLike(item.optionConfiguration)
                    ? 'saleItemOption.update'
                    : 'saleItem.update',
                data: { id: item.id }
            } },
            label.title,
            label.infos ? (React.createElement("span", { className: "sale-item-availability-section-infos" }, label.infos)) : null));
    }
    return (React.createElement("div", { className: "sale-item-availability-section" },
        React.createElement(FormGroup, null,
            React.createElement(FormField, { ratio: 6 },
                React.createElement(FormSwitch, { name: "exclusiveTimeSlot", label: t('Exclusive booking'), infos: linked
                        ? t('Always exclusive while it is linked to other items.')
                        : t('Only one active or won deal can book this item at a time: on its time slot, or for the whole day when it has no time.'), active: linked || ((_c = data === null || data === void 0 ? void 0 : data.exclusiveTimeSlot) !== null && _c !== void 0 ? _c : false), disabled: linked, required: true }))),
        bookedBy.length > 0 ? (React.createElement(FormGroup, null,
            React.createElement(FormField, null,
                React.createElement("div", { className: "sale-item-availability-section-label" }, t('Also booked by')),
                React.createElement("ul", { className: "sale-item-availability-section-list" }, bookedBy.map((item) => (React.createElement("li", { key: item.id }, renderItem(item))))),
                React.createElement("div", { className: "sale-item-availability-section-note" }, t('An item booked by another item cannot book other items itself.'))))) : (React.createElement(FormGroup, null,
            React.createElement(FormField, null,
                React.createElement(FormInputPicker, { label: t('Also books'), infos: t('Booking this item also books the chosen items: they can no longer be booked at the same time elsewhere, and vice versa.'), placeholder: t('Select an option or a product'), query: SaleItemsListLightQuery, objectType: "saleItems", queryOptions: {
                        optionConfiguration: [
                            ...PRODUCT_LIST_CONFIGURATIONS,
                            'OPTIONNABLE',
                            ...OPTION_LIST_CONFIGURATIONS
                        ],
                        active: true,
                        order: primarySaleItemInfo == 'SKU' ? 'sku:asc' : 'title:asc'
                    }, idsToExclude: (data === null || data === void 0 ? void 0 : data.id) ? [data.id, ...ids] : ids, format: format, values: items, onSelect: (item) => add([item]), onRemove: (item) => remove([item]), onChangeMany: (added, removed) => {
                        remove(removed);
                        add(added);
                    } }),
                items.length > 0 && (React.createElement("ul", { className: "sale-item-availability-section-list" }, items.map((item) => (React.createElement("li", { key: item.id },
                    renderItem(item),
                    React.createElement(Button, { style: "picto", size: "small", picto: "trash-can", onPress: () => remove([item]) })))))))))));
}
